import { BASE_UNIT_LABELS } from "@medicorepay/shared";
import {
  BadgePercent,
  Boxes,
  CheckCircle,
  Keyboard,
  Minus,
  Package,
  Plus,
  ShoppingCart,
  Sparkles,
  Trash2,
} from "lucide-react";

interface ComputedCartItem {
  product: {
    id: string;
    name: string;
    manufacturer?: string | null;
    scheduleType?: string | null;
    unit?: string;
    baseUnitType?: string | null;
    mrp?: number;
    sku?: string;
  };
  quantity: number;
  rate: number;
  batchId?: string;
  batchStock: number;
  discountType: "AMOUNT" | "PERCENT";
  discountValue: number;
  gstPercent: number;
  gstAmount: number;
  total: number;
  manualDiscount: number;
  schemeDiscount: number;
  schemeFreeQuantity: number;
  salePackId?: string;
  unitsPerPack: number;
  packLabel: string;
  availablePacks: Array<{
    id: string;
    packDescription: string;
    unitsPerPack: number;
    sellingPrice: number;
  }>;
}

interface Totals {
  subtotal: number;
  totalGst: number;
  grandTotal: number;
  manualDiscountTotal: number;
  schemeDiscountTotal: number;
  billDiscountAmount: number;
}

interface CartTableProps {
  cart: any[];
  computedCart: ComputedCartItem[];
  totals: Totals;
  updateQuantity: (index: number, qty: number) => void;
  updateDiscount: (index: number, value: number) => void;
  updateDiscountType: (index: number, type: "AMOUNT" | "PERCENT") => void;
  updatePack: (index: number, packId: string | undefined) => void;
  removeFromCart: (index: number) => void;
}

function getScheduleBadge(type: string | undefined | null) {
  if (!type || type === "GENERAL") return null;
  const styles: Record<string, string> = {
    SCHEDULE_H: "border-orange-200 bg-orange-50 text-orange-700",
    SCHEDULE_H1: "border-red-200 bg-red-50 text-red-700",
    SCHEDULE_X: "border-purple-200 bg-purple-50 text-purple-700",
    SCHEDULE_G: "border-blue-200 bg-blue-50 text-blue-700",
  };
  return {
    label: type.replace("SCHEDULE_", "Sch. "),
    className: styles[type] ?? "border-slate-200 bg-slate-50 text-slate-600",
  };
}

function getBaseUnitLabel(product: ComputedCartItem["product"]): string {
  const baseUnitType = product.baseUnitType?.toUpperCase() as
    | keyof typeof BASE_UNIT_LABELS
    | undefined;

  return (
    (baseUnitType ? BASE_UNIT_LABELS[baseUnitType] : undefined) ||
    product.unit ||
    "Unit"
  );
}

export function CartTable({
  cart,
  computedCart,
  totals,
  updateQuantity,
  updateDiscount,
  updateDiscountType,
  updatePack,
  removeFromCart,
}: CartTableProps) {
  const totalDiscount =
    totals.manualDiscountTotal +
    totals.schemeDiscountTotal +
    totals.billDiscountAmount;
  const totalUnits = computedCart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <header className="flex min-h-11 shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-teal-600" />
            <h2 className="text-sm font-semibold text-slate-900">
              Current bill
            </h2>
          </div>
          <p className="text-[11px] text-slate-500">
            {cart.length === 0
              ? "No products added yet"
              : `${cart.length} product${cart.length === 1 ? "" : "s"} · ${totalUnits} unit${totalUnits === 1 ? "" : "s"}`}
          </p>
        </div>

        {totalDiscount > 0 && (
          <div className="flex items-center gap-1.5 rounded-lg border border-emerald-100 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700">
            <Sparkles className="h-3.5 w-3.5" />
            Saving ₹{totalDiscount.toFixed(2)}
          </div>
        )}
        {cart.length > 0 && totalDiscount <= 0 && (
          <span className="hidden items-center gap-1.5 text-[10px] font-medium text-teal-700 xl:flex">
            <CheckCircle className="h-3.5 w-3.5" />
            Stock and quantity validated
          </span>
        )}
      </header>

      {cart.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100">
            <ShoppingCart className="h-7 w-7 text-slate-300" />
          </div>
          <h3 className="text-base font-semibold text-slate-800">
            Ready for billing
          </h3>
          <p className="mt-1 max-w-sm text-sm leading-relaxed text-slate-500">
            Scan a barcode or search above. The earliest-expiring in-stock batch
            is selected automatically.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <Keyboard className="h-3.5 w-3.5" />
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px]">
                F2
              </kbd>
              Search
            </span>
            <span className="flex items-center gap-1.5">
              <Package className="h-3.5 w-3.5" />
              Barcode quick-add
            </span>
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full min-w-[760px] table-fixed text-left text-sm">
            <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 text-[10px] font-semibold uppercase tracking-wider text-slate-500 backdrop-blur">
              <tr>
                <th className="w-[36%] px-4 py-2">Product &amp; stock</th>
                <th className="w-[15%] px-2 py-2 text-center">Quantity</th>
                <th className="w-[13%] px-3 py-2 text-right">Rate</th>
                <th className="w-[18%] px-3 py-2 text-center">
                  Discount &amp; GST
                </th>
                <th className="w-[13%] px-3 py-2 text-right">Amount</th>
                <th className="w-[5%] px-2 py-2">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {computedCart.map((item, index) => {
                const schedule = getScheduleBadge(item.product.scheduleType);
                const unitsPerPack = Math.max(1, item.unitsPerPack || 1);
                const availablePackQuantity =
                  item.batchStock > 0
                    ? Math.floor(item.batchStock / unitsPerPack)
                    : null;

                return (
                  <tr
                    key={`${item.product.id}-${item.batchId ?? "auto"}-${index}`}
                    className="group align-middle transition-colors hover:bg-teal-50/30"
                  >
                    <td className="px-4 py-2">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-600">
                          <Package className="h-3.5 w-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="truncate text-[13px] font-semibold text-slate-900">
                              {item.product.name}
                            </span>
                            {schedule && (
                              <span
                                className={`shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-semibold ${schedule.className}`}
                              >
                                {schedule.label}
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 flex min-w-0 items-center gap-1.5 overflow-hidden text-[10px] text-slate-500">
                            {item.product.sku && (
                              <span className="max-w-[110px] truncate font-mono">
                                {item.product.sku}
                              </span>
                            )}
                            {item.availablePacks.length > 0 ? (
                              <select
                                aria-label={`Pack for ${item.product.name}`}
                                value={item.salePackId || ""}
                                onChange={(e) =>
                                  updatePack(index, e.target.value || undefined)
                                }
                                className="h-6 max-w-[92px] shrink-0 rounded-md border border-slate-200 bg-white px-1 text-[9px] font-medium text-slate-700 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
                              >
                                <option value="">
                                  {getBaseUnitLabel(item.product)}
                                </option>
                                {item.availablePacks.map((pack) => (
                                  <option key={pack.id} value={pack.id}>
                                    {pack.packDescription}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span>{item.packLabel}</span>
                            )}
                            <span className="flex shrink-0 items-center gap-1 text-teal-700">
                              <Boxes className="h-3 w-3" />
                              {availablePackQuantity === null
                                ? "Stock validated"
                                : `${availablePackQuantity} available`}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-2 py-2 text-center">
                      <div className="inline-grid h-8 grid-cols-[26px_34px_26px] items-center overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                        <button
                          type="button"
                          aria-label={`Decrease ${item.product.name} quantity`}
                          onClick={() =>
                            updateQuantity(index, item.quantity - 1)
                          }
                          className="flex h-full items-center justify-center text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <input
                          type="number"
                          min={1}
                          aria-label={`Quantity for ${item.product.name}`}
                          value={item.quantity}
                          onChange={(e) =>
                            updateQuantity(index, parseInt(e.target.value) || 1)
                          }
                          className="h-full w-full border-x border-slate-200 bg-slate-50/60 text-center text-sm font-semibold text-slate-900 outline-none focus:bg-teal-50"
                        />
                        <button
                          type="button"
                          aria-label={`Increase ${item.product.name} quantity`}
                          onClick={() =>
                            updateQuantity(index, item.quantity + 1)
                          }
                          className="flex h-full items-center justify-center text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      {item.schemeFreeQuantity > 0 && (
                        <div className="mt-1 text-[10px] font-semibold text-emerald-600">
                          +{item.schemeFreeQuantity} free
                        </div>
                      )}
                    </td>

                    <td className="px-3 py-2 text-right">
                      <div className="text-[13px] font-semibold tabular-nums text-slate-800">
                        ₹{item.rate.toFixed(2)}
                      </div>
                      <div className="mt-0.5 text-[10px] tabular-nums text-slate-400">
                        MRP ₹{Number(item.product.mrp || item.rate).toFixed(2)}
                      </div>
                    </td>

                    <td className="px-3 py-2 text-center">
                      <div className="inline-flex h-7 items-center overflow-hidden rounded-lg border border-slate-200 bg-white">
                        <select
                          aria-label={`Discount type for ${item.product.name}`}
                          value={item.discountType}
                          onChange={(e) =>
                            updateDiscountType(
                              index,
                              e.target.value as "AMOUNT" | "PERCENT",
                            )
                          }
                          className="h-full border-0 bg-slate-50 px-1.5 text-xs font-medium text-slate-600 outline-none"
                        >
                          <option value="AMOUNT">₹</option>
                          <option value="PERCENT">%</option>
                        </select>
                        <input
                          type="number"
                          min={0}
                          aria-label={`Discount for ${item.product.name}`}
                          value={item.discountValue || ""}
                          onChange={(e) =>
                            updateDiscount(
                              index,
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          placeholder="0"
                          className="h-full w-14 border-l border-slate-200 px-2 text-right text-xs font-medium tabular-nums text-slate-700 outline-none focus:bg-teal-50"
                        />
                      </div>
                      <div className="mt-0.5 flex items-center justify-center gap-1 text-[10px] text-slate-500">
                        <BadgePercent className="h-3 w-3" />
                        GST {item.gstPercent.toFixed(0)}%
                        {item.schemeDiscount > 0 && (
                          <span className="font-semibold text-emerald-600">
                            · Offer −₹{item.schemeDiscount.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-3 py-2 text-right">
                      <span className="text-[13px] font-semibold tabular-nums text-slate-900">
                        ₹{item.total.toFixed(2)}
                      </span>
                    </td>

                    <td className="px-2 py-2 text-center">
                      <button
                        type="button"
                        aria-label={`Remove ${item.product.name}`}
                        onClick={() => removeFromCart(index)}
                        className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-300 transition hover:bg-red-50 hover:text-red-500 focus-visible:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
